import DOMPurify from 'dompurify';

/**
 * Formats a single line of plain text into inline HTML.
 *
 * Handles HTML escaping and the following Markdown-like syntax:
 * - `**text**` → `<strong>text</strong>` (bold)
 * - `_text_` → `<em>text</em>` (italic, not triggered mid-word)
 * - `[text](url)` → `<a href="...">text</a>` (links; prepends `https://` when no protocol is present)
 *
 * @param text - Raw text to process.
 * @returns HTML string with inline formatting applied. The output is NOT sanitized;
 *   callers are responsible for sanitizing before injecting into the DOM.
 */
export function formatInline(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Bold: **text**
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic: _text_
  html = html.replace(/(?<!\w)_(.+?)_(?!\w)/g, '<em>$1</em>');
  // Links: [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
    const href = /^https?:\/\//.test(url) ? url : `https://${url}`;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
  });

  return html;
}

/**
 * Converts a plain-text message body into sanitized HTML suitable for
 * `dangerouslySetInnerHTML`.
 *
 * Formatting rules applied in order:
 * 1. The text is split into blocks on two or more consecutive newlines (`\n\n+`).
 * 2. Each block is examined:
 *    - If every line matches an ordered-list pattern (`1. item`), the block is
 *      rendered as an `<ol>` with `<li>` items.
 *    - Otherwise the block is wrapped in a `<p>`, with single newlines converted
 *      to `<br>` tags.
 * 3. Inline formatting (`formatInline`) is applied to each line/block.
 * 4. The resulting HTML is sanitized with DOMPurify (XSS prevention), with the
 *    `target` attribute preserved so link `target="_blank"` survives sanitization.
 *
 * @param text - Raw message body text.
 * @returns Sanitized HTML string ready for injection into the DOM.
 */
export function formatMessageBody(text: string): string {
  const blocks = text.split(/\n\n+/);

  const htmlBlocks = blocks.map((block) => {
    const lines = block.split('\n');
    // Check if all lines are ordered list items (e.g. "1. item")
    const isOrderedList = lines.every((line) => /^\d+\.\s/.test(line.trim()));
    if (isOrderedList) {
      const items = lines
        .map((line) => `<li>${formatInline(line.replace(/^\d+\.\s/, ''))}</li>`)
        .join('');
      return `<ol>${items}</ol>`;
    }
    return `<p>${formatInline(block).replace(/\n/g, '<br>')}</p>`;
  });

  return DOMPurify.sanitize(htmlBlocks.join(''), {
    ADD_ATTR: ['target'],
  });
}
